import { useEffect, useMemo, useState, type FormEvent } from "react";
import { CalendarCheck, ChevronLeft, ChevronRight, Globe, LoaderCircle, Video } from "lucide-react";
import { ApiError, api } from "../../api";
import { WEEKDAY_LABELS, addMonths, monthLabel, monthMatrix, slotsByDay, tzLabel, viewerTz, whenLabel } from "../../crm";
import { notifyParent, visitMeta } from "./publicPage";

export type PublicCalendar = { slug: string; name: string; description: string; duration_min: number; timezone: string; location_kind: string; /** A booking gets a Google event and an emailed invite. */ invites?: boolean };
export type BookedResult = { starts_at: string; ends_at: string; meeting_url: string; manage_path: string; email: string; invite_sent: boolean };

/** US zones first; the visitor's own zone is added when it isn't one of them. */
const ZONES = ["America/New_York", "America/Chicago", "America/Denver", "America/Phoenix", "America/Los_Angeles", "America/Anchorage", "Pacific/Honolulu"];

/**
 * Calendly-style picker: a month with the open days marked, then the times for the
 * picked day in the visitor's zone. A lead who came from a form books with its
 * `bookToken` (no second form); anyone else gives a name, email and phone.
 */
export default function BookingPicker({ calendar, bookToken, onBook, onBooked, slotsPath }: {
  calendar: PublicCalendar;
  bookToken?: string;
  /** Staff booking for a known contact: called instead of the public booking endpoint. */
  onBook?: (startIso: string, timezone: string) => Promise<BookedResult>;
  onBooked: (result: BookedResult) => void;
  /** Where the open times come from (staff use their own endpoint); the public one by default. */
  slotsPath?: string;
}) {
  const known = Boolean(bookToken || onBook);
  const [tz, setTz] = useState(viewerTz);
  const [slots, setSlots] = useState<string[] | null>(null);
  /** Times could not load (the whole picker shows it). */
  const [loadError, setLoadError] = useState("");
  /** The booking itself failed (shown beside the button; survives a reload of the times). */
  const [error, setError] = useState("");
  const [month, setMonth] = useState<{ y: number; m: number } | null>(null);
  const [day, setDay] = useState<string | null>(null);
  const [picked, setPicked] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [details, setDetails] = useState({ first_name: "", last_name: "", email: "", phone: "", notes: "" });
  const [reload, setReload] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setSlots(null);
    setLoadError("");
    api<{ slots: string[] }>(slotsPath || `/api/public/crm/book/${encodeURIComponent(calendar.slug)}/slots`, { signal: controller.signal })
      .then((r) => setSlots(r.slots))
      .catch((e: unknown) => { if (!controller.signal.aborted) setLoadError(e instanceof Error ? e.message : "Times could not load."); });
    return () => controller.abort();
  }, [calendar.slug, slotsPath, reload]);

  const byDay = useMemo(() => slotsByDay(slots || [], tz), [slots, tz]);
  const firstDay = useMemo(() => [...byDay.keys()][0] || null, [byDay]);
  // Open on the month of the first open day, and pick that day.
  useEffect(() => {
    if (!slots) return;
    if (firstDay) {
      const [y, m] = firstDay.split("-").map(Number);
      setMonth((cur) => cur || { y, m });
      setDay((cur) => (cur && byDay.has(cur) ? cur : firstDay));
    } else {
      const now = new Date();
      setMonth((cur) => cur || { y: now.getFullYear(), m: now.getMonth() + 1 });
    }
  }, [slots, firstDay, byDay]);

  const zones = ZONES.includes(tz) ? ZONES : [tz, ...ZONES];
  const times = day ? byDay.get(day) || [] : [];

  async function book(event?: FormEvent) {
    event?.preventDefault();
    if (!picked) return;
    setBusy(true);
    setError("");
    try {
      const result = onBook ? await onBook(picked, tz) : await api<BookedResult>(`/api/public/crm/book/${encodeURIComponent(calendar.slug)}`, {
        method: "POST",
        // A direct booking (no form first) carries the visit, so its ad click reaches the contact.
        body: JSON.stringify({ start: picked, timezone: tz, book_token: bookToken || undefined, contact: bookToken ? undefined : details, meta: bookToken ? undefined : visitMeta() }),
      });
      onBooked(result);
      // A NOLA landing page framing this calendar (or the form it sits in) moves its funnel on.
      if (!onBook) notifyParent({ type: "nla-booked" });
    } catch (e) {
      setError(e instanceof Error ? e.message : "That time could not be booked.");
      // A taken slot: fetch fresh times so the list matches the calendar again. Any
      // other problem (a typo in the phone, too many tries) keeps the form and what was typed.
      if (e instanceof ApiError && e.status === 409) {
        setPicked(null);
        setReload((n) => n + 1);
      }
    } finally {
      setBusy(false);
    }
  }

  if (loadError && !slots) return <p className="bk-note" role="alert">{loadError}</p>;
  if (!slots || !month) return <p className="bk-note" role="status"><LoaderCircle className="spin" aria-hidden="true" />Loading times</p>;
  if (!slots.length) return <p className="bk-note">No times are open right now. We will reach out to set one up.</p>;

  const weeks = monthMatrix(month.y, month.m);
  const set = (k: keyof typeof details) => (e: { target: { value: string } }) => setDetails((d) => ({ ...d, [k]: e.target.value }));

  return (
    <div className="bk">
      <div className="bk-cal">
        <div className="bk-month">
          <button type="button" className="bk-nav" aria-label="Previous month" onClick={() => setMonth(addMonths(month.y, month.m, -1))}><ChevronLeft aria-hidden="true" /></button>
          <strong>{monthLabel(month.y, month.m)}</strong>
          <button type="button" className="bk-nav" aria-label="Next month" onClick={() => setMonth(addMonths(month.y, month.m, 1))}><ChevronRight aria-hidden="true" /></button>
        </div>
        <div className="bk-grid" role="grid" aria-label={monthLabel(month.y, month.m)}>
          {WEEKDAY_LABELS.map((w) => <span key={w} className="bk-wd" aria-hidden="true">{w.slice(0, 2)}</span>)}
          {weeks.flat().map((cell) => {
            const open = cell.inMonth && byDay.has(cell.key);
            return (
              <button
                type="button"
                key={cell.key}
                className={`bk-day${open ? " open" : ""}${day === cell.key ? " on" : ""}${cell.inMonth ? "" : " out"}`}
                disabled={!open}
                aria-pressed={day === cell.key}
                aria-label={`${cell.key}${open ? ", times open" : ""}`}
                onClick={() => { setDay(cell.key); setPicked(null); setError(""); }}
              >
                {cell.d}
              </button>
            );
          })}
        </div>
        <label className="bk-tz">
          <Globe aria-hidden="true" />
          <select value={tz} onChange={(e) => { setTz(e.target.value); setPicked(null); }} aria-label="Time zone">
            {zones.map((z) => <option key={z} value={z}>{tzLabel(z)} time</option>)}
          </select>
        </label>
      </div>
      <div className="bk-times">
        {picked && !known ? (
          <form className="bk-details" onSubmit={book}>
            <button type="button" className="bk-back" onClick={() => setPicked(null)}><ChevronLeft aria-hidden="true" />{whenLabel(picked, tz)}</button>
            <div className="bk-row">
              <label>First name<input value={details.first_name} onChange={set("first_name")} required autoComplete="given-name" maxLength={80} /></label>
              <label>Last name<input value={details.last_name} onChange={set("last_name")} autoComplete="family-name" maxLength={80} /></label>
            </div>
            <label>Email<input type="email" value={details.email} onChange={set("email")} required autoComplete="email" maxLength={200} /></label>
            <label>Phone<input type="tel" value={details.phone} onChange={set("phone")} required autoComplete="tel" maxLength={40} /></label>
            <label><span>Anything we should know? <span className="bk-optional">Optional</span></span><textarea value={details.notes} onChange={set("notes")} rows={3} maxLength={1000} /></label>
            {error && <p className="bk-error" role="alert">{error}</p>}
            <button className="bk-confirm" type="submit" disabled={busy}>{busy ? "Booking…" : "Confirm booking"}</button>
          </form>
        ) : (
          <>
            <p className="bk-day-label">{day ? whenLabel(`${day}T12:00:00Z`, "UTC").replace(/ at .*/, "") : "Pick a day"}</p>
            {/* A problem shows where the eye is: under the picked time, or above the list after the times reload. */}
            {error && !picked && <p className="bk-error bk-error-top" role="alert">{error}</p>}
            <div className="bk-slots">
              {times.map((t) => (
                <div key={t.iso} className={`bk-slot${picked === t.iso ? " on" : ""}`}>
                  <button type="button" onClick={() => { setPicked(t.iso); setError(""); }} aria-pressed={picked === t.iso}>{t.label}</button>
                  {picked === t.iso && known && (
                    <button type="button" className="bk-slot-go" disabled={busy} onClick={() => void book()}>{busy ? "Booking…" : "Confirm"}</button>
                  )}
                  {picked === t.iso && error && <p className="bk-error bk-slot-error" role="alert">{error}</p>}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * What the visitor sees once the call is booked. `staff` is the same card in the
 * contact drawer: staff wording, Eastern time, and no "change it" link (Reschedule is in the drawer).
 */
export function BookedPanel({ result, calendar, staff = false }: { result: BookedResult; calendar: PublicCalendar; staff?: boolean }) {
  const tz = staff ? "America/New_York" : viewerTz();
  const next = result.invite_sent
    ? `A calendar invite is on its way to ${result.email}.`
    : staff
      ? calendar.location_kind === "phone" ? "No invite was sent. Call them at that time." : "No invite was sent, so send them the call details."
      : calendar.location_kind === "phone" ? "We will call you then." : "We will send you the call details before then.";
  return (
    <div className="bk-done" role="status">
      <span className="bk-done-icon"><CalendarCheck aria-hidden="true" /></span>
      <h2>{staff ? "Call booked" : "You are booked"}</h2>
      <p className="bk-done-when">{whenLabel(result.starts_at, tz)}{staff ? " ET" : <> <span>({tzLabel(tz)} time)</span></>}</p>
      <p>{calendar.name}, {calendar.duration_min} minutes. {next}</p>
      {result.meeting_url && <a className="bk-link" href={result.meeting_url} target="_blank" rel="noreferrer"><Video aria-hidden="true" />Video call link</a>}
      {!staff && <a className="bk-manage" href={result.manage_path}>Need to change it? Cancel or pick a new time</a>}
    </div>
  );
}
